/* eslint-disable max-lines */
import { VisitInfoImage, VisitTimeImage, VisitTrainImage } from '@local/assets/images'
import { extractByTypename } from '@local/utils'

import {
  ConvertToUiReviewSnippet_ReviewGroupFragment,
  ConvertToUiTimelineBookingItem_TimelineFragment,
  ConvertToUiTimelineDateItem_TimelineFragment,
  ConvertToUiTimelineDisplayName_TimelineFragment,
  ConvertToUiTimelineProductItem_TimelineFragment,
  Timeline,
  TimelineListOfDay_TimelineFragment,
  TimelineType,
} from '@web/federation/gql/graphql'
import { gql } from '@web/gql'
import { ReviewGroupExposeStatus } from '@web/gql/graphql'
import { OrderTimelineType } from '@web/libs/constants'
import { formatDisplayVisitTime } from '@web/libs/formatDisplayVisitTime'
import { calculateStay } from '@web/libs/formatter'
import { getDisplayVisitDate } from '@web/libs/getDisplayVisitDate'
import { bookingIconUrlMap } from '@web/libs/timeline/iconMap'
import { ArrayElement } from '@web/types'
import { FunctionButtonTypes } from '@web/ui/components/FunctionButton'
import { ReviewSnippetProps } from '@web/ui/pages/timeline/components/TimelineSection/components/TimelineItemContent/TimelineReviewSnippet'
import { UITimelineSectionCardProps } from '@web/ui/pages/timeline/components/TimelineSection/components/TimelineItemContent/TimelineSectionCard'

import TrainTicketDisplayName from '../TrainTicketDisplayName/TrainTicketDisplayName'

export const TIMELINE_TIME_FORMAT = 'A h:mm'

export const isReviewWritableTimeline = (timeline: Pick<Timeline, 'type'>) => timeline.type !== TimelineType.TrainTicket

// eslint-disable-next-line no-unused-vars
const timelineTypename = gql(`
  fragment TimelineTypeName_Type on Timeline {
    id
    type
  }
`)

export const convertToButtonTypes = (timeline: TimelineListOfDay_TimelineFragment): FunctionButtonTypes[] => {
  if (!timeline.__typename) return []

  const { OrderTimeline, ReservationTimeline } = extractByTypename(timeline)

  const isReviewWritable = isReviewWritableTimeline(timeline)
  const reBookingUrl = ReservationTimeline?.reBookingUrl ?? OrderTimeline?.reBookingUrl
  const bookingDetailUrl = ReservationTimeline?.bookingDetailUrl ?? OrderTimeline?.bookingDetailUrl

  const buttons: FunctionButtonTypes[] = []
  if (OrderTimeline) {
    if (reBookingUrl) buttons.push('ORDER')

    if (bookingDetailUrl) buttons.push('ORDER_HISTORY')
  }

  if (ReservationTimeline) {
    if (reBookingUrl) buttons.push('RESERVATION')

    if (bookingDetailUrl) buttons.push('RESERVATION_HISTORY')
  }

  if (isReviewWritable && !timeline.reviewGroup?.id) {
    buttons.push('REVIEW_WRITE')
  }

  return buttons
}

export const convertToUITimelineDisplayName = (
  timeline: ConvertToUiTimelineDisplayName_TimelineFragment,
): UITimelineSectionCardProps['displayName'] => {
  const { TrainTicketTimeline } = extractByTypename(timeline)
  if (TrainTicketTimeline) {
    const stationNames =
      TrainTicketTimeline.stationNames.length > 0
        ? TrainTicketTimeline.stationNames
        : TrainTicketTimeline.displayName.split(',')

    return <TrainTicketDisplayName stationNames={stationNames} />
  }

  return timeline.displayName
}

convertToUITimelineDisplayName.fragments = gql(`
  fragment convertToUITimelineDisplayName_Timeline on Timeline {
    id
    displayName
    ... on TrainTicketTimeline {
      stationNames
    }
  }
`)

export const convertToUITimelineDateItem = (
  timeline: ConvertToUiTimelineDateItem_TimelineFragment,
): ArrayElement<UITimelineSectionCardProps['infoItems']> | null => {
  // ReservationTimeline 기간형 예약상품 표기는 Phase1에서 미대응
  const { HotelTimeline, TrainTicketTimeline, ReservationTimeline } = extractByTypename(timeline)

  if (HotelTimeline && HotelTimeline.eventDateTimeRange) {
    const checkInDate = getDisplayVisitDate(HotelTimeline.eventDateTimeRange.from).displayDateTime
    const checkOutDate = getDisplayVisitDate(HotelTimeline.eventDateTimeRange.to).displayDateTime
    const dateTimeLabel = `${checkInDate}- ${checkOutDate}`

    const nNights = calculateStay(HotelTimeline.eventDateTimeRange.from, HotelTimeline.eventDateTimeRange.to)

    return {
      iconUrl: VisitTimeImage.src,
      name: dateTimeLabel,
      description: nNights,
    }
  }

  if (TrainTicketTimeline) {
    const { eventDateTimeRange } = TrainTicketTimeline
    const timeLabel = eventDateTimeRange
      ? formatDisplayVisitTime({
          dateTimeISO: eventDateTimeRange.from,
          timeFormat: TIMELINE_TIME_FORMAT,
          endDateTimeISO: eventDateTimeRange.to,
        }).displayTime
      : null

    return {
      iconUrl: VisitTimeImage.src,
      name: timeLabel,
    }
  }

  const displayTime = formatDisplayVisitTime({
    dateTimeISO: timeline.eventDateTime,
    timeFormat: TIMELINE_TIME_FORMAT,
  }).displayTime

  const displayTimeLabel = ReservationTimeline ? `${displayTime} 예약` : displayTime

  if (!('visitCount' in timeline)) {
    return {
      iconUrl: VisitTimeImage.src,
      name: displayTimeLabel,
    }
  }

  const visitCount = timeline.visitCount
  const revisitIntervalText =
    'revisitInterval' in timeline && timeline.revisitInterval ? `, ${timeline.revisitInterval}일만의` : ''

  return {
    iconUrl: VisitTimeImage.src,
    name: displayTimeLabel,
    description: visitCount ? `${visitCount}번째 ${revisitIntervalText} 방문` : null,
  }
}

convertToUITimelineDateItem.fragments = gql(`
  fragment convertToUITimelineDateItem_Timeline on Timeline {
    id
    displayName
    eventDateTime
    ... on HotelTimeline {
      eventDateTimeRange {
        from
        to
      }
    }
    ... on TrainTicketTimeline {
      eventDateTimeRange {
        from
        to
      }
    }
    ... on ReceiptTimeline {
      revisitInterval
      visitCount
    }
    ... on CheckInTimeline {
      revisitInterval
      visitCount
    }
    ... on PaymentTimeline {
      revisitInterval
      visitCount
    }
    ... on OrderTimeline {
      revisitInterval
      visitCount
    }
    ... on ReservationTimeline {
      revisitInterval
      visitCount
    }
    ... on NFTTimeline {
      visitCount
      revisitInterval
    }
  }
`)

const formatPriceLabel = (price: number, currencyCode = 'KRW') => {
  if (price === 0) return null

  if (currencyCode === 'KRW') return `${price.toLocaleString()}원`

  return `${price}${currencyCode}`
}

export const convertToUITimelineProductItem = (
  timeline: ConvertToUiTimelineProductItem_TimelineFragment,
): ArrayElement<UITimelineSectionCardProps['infoItems']> | null => {
  const { TrainTicketTimeline } = extractByTypename(timeline)
  if (TrainTicketTimeline) {
    const { trainNames } = TrainTicketTimeline
    const trainCountText = trainNames.length > 1 ? `외 ${trainNames.length - 1}` : ''

    return {
      iconUrl: VisitTrainImage.src,
      name: trainNames[0] ? `${trainNames[0]} ${trainCountText}` : null,
      description: TrainTicketTimeline.totalPrice ? `${TrainTicketTimeline.totalPrice?.toLocaleString()}원` : null,
    }
  }

  // NOTE: 타입 헬퍼 개선해보기
  // const { CheckInTimeline, ...restTimeline } = extractByTypename(timeline)
  // if (CheckInTimeline) {
  //   return null
  // }

  const mainProductName = 'mainProductName' in timeline ? timeline.mainProductName : null
  const totalPrice = 'totalPrice' in timeline ? timeline.totalPrice : null
  const currencyCode = 'currencyCode' in timeline ? timeline.currencyCode : undefined

  const productCountText =
    'productCount' in timeline && timeline.productCount > 1 ? `외 ${timeline.productCount - 1}` : null

  if (!totalPrice && !mainProductName) {
    return null
  }

  return {
    iconUrl: VisitInfoImage.src,
    name: mainProductName,
    countText: productCountText,
    description: totalPrice ? formatPriceLabel(totalPrice, currencyCode) : null,
  }
}

convertToUITimelineProductItem.fragments = gql(`
  fragment convertToUITimelineProductItem_Timeline on Timeline {
    id
    displayName
    eventDateTime
    ... on TrainTicketTimeline {
      trainNames
      totalPrice
      currencyCode
    }
    ... on ReceiptTimeline {
      mainProductName
      productCount
      totalPrice
      currencyCode
    }
    ... on OrderTimeline {
      mainProductName
      productCount
      totalPrice
      currencyCode
    }
    ... on ReservationTimeline {
      mainProductName
      productCount
      totalPrice
      currencyCode
    }
    ... on PaymentTimeline {
      totalPrice
      currencyCode
    }
    ... on HotelTimeline {
      totalPrice
      currencyCode
    }
    ... on NFTTimeline {
      mainProductName
    }
  }
`)

export const convertToUITimelineBookingItem = (
  timeline: ConvertToUiTimelineBookingItem_TimelineFragment,
): ArrayElement<UITimelineSectionCardProps['infoItems']> | null => {
  const { OrderTimeline } = extractByTypename(timeline)

  if (!OrderTimeline || !OrderTimeline.bookingTypeLabel) {
    return null
  }

  const iconUrl = bookingIconUrlMap[OrderTimeline.type as OrderTimelineType]?.iconUrl

  if (!iconUrl) {
    return null
  }

  return {
    iconUrl,
    name: OrderTimeline.bookingTypeLabel,
  }
}

convertToUITimelineBookingItem.fragments = gql(`
  fragment convertToUITimelineBookingItem_Timeline on Timeline {
    id
    ... on OrderTimeline {
      type
      bookingTypeIconUrl {
        svg
      }
      bookingTypeLabel
    }
  }
`)

export const convertToUIReviewSnippet = (review: ConvertToUiReviewSnippet_ReviewGroupFragment): ReviewSnippetProps => {
  const { mainMediaItem, mediaCount, rating, text, votedKeywords, exposeStatus } = review

  const { ImageItem, VideoItem } = extractByTypename(mainMediaItem)
  const displayMediaUrl = ImageItem?.url ?? VideoItem?.thumbnail

  return {
    mainMediaUrl: displayMediaUrl,
    mediaCount,
    rating,
    text,
    keywords: votedKeywords.map(keyword => ({
      text: keyword.text.korean,
      iconUrl: keyword.icon,
    })),
    isPermanentSuspended: exposeStatus === ReviewGroupExposeStatus.UnexposePermanently,
    isTempSuspended: exposeStatus === ReviewGroupExposeStatus.UnexposeTemporarily,
  }
}

convertToUIReviewSnippet.fragments = gql(`
  fragment convertToUIReviewSnippet_ReviewGroup on ReviewGroup {
    id
    mediaCount
    rating
    text
    exposeStatus
    mainMediaItem {
      id
      ... on ImageItem {
        url
      }
      ... on VideoItem {
        thumbnail
      }
    }
    votedKeywords {
      text {
        korean
      }
      icon
    }
  }
`)

export const convertToUITimelinePlaceInfo = (timeline: TimelineListOfDay_TimelineFragment) => {
  const { HotelTimeline, TrainTicketTimeline } = extractByTypename(timeline)
  const [categoryName, shortAddress] = timeline.categoryLabels

  if (TrainTicketTimeline || HotelTimeline) {
    return { categoryName, shortAddress }
  }

  const isClosedPlace = timeline.placeSummary?.notExist === true
  return isClosedPlace ? null : { categoryName, shortAddress }
}
